import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useLocation } from "react-router-dom";
import { useTheme } from "../Navbar/Theme";

// ── Sound Utilities & Dedicated Audio Assets ──
import { sounds } from "../Utils/soundEffects";
import miniHoverSfx from "../../assets/sounds/whooshss.mp3";
import miniClickSfx from "../../assets/sounds/mini-click.mp3";
import marqueeHoverSfx from "../../assets/sounds/node-hover.mp3";
import marqueeClickSfx from "../../assets/sounds/tap-chime.mp3";
import paperHoverSfx from "../../assets/sounds/node-hover.mp3";
import generalClickSfx from "../../assets/sounds/tap-main.mp3";

// Assets
import devcover from "../../assets/nuvexcard.webp";
import gdcover from "../../assets/vdcover.webp";
import wayfare from "../../assets/wayfare.webp";
import evento from "../../assets/evento.webp";
import evento1 from "../../assets/evento1.webp";
import bio from "../../assets/bio.webp";
import verd from "../../assets/verd1.webp";
import iunides from "../../assets/iunides.webp";
import iunidev from "../../assets/iunidev.webp";
import ayurnest from "../../assets/ayurnest.webp";
import nuvexcover from "../../assets/nuvexcover.webp";
import petty from "../../assets/petty.webp";
import postercover from "../../assets/postercover.webp";
import researchcover from "../../assets/dronecov.webp";

// Direct Static Imports
import UIUXPage from "./UIUXPage";
import Front from "./Front";
import Graphics from "./Graphics";

import "../styles/Work.css";

const hexToRgb = (hex) => {
  const clean = hex.trim().replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return { r: r || 0, g: g || 0, b: b || 0 };
};

const ZONE_POSITIONS = [
  { x: 15, y: -10 },
  { x: 18, y: -90 },
  { x: 88, y: -15 },
];

const RIB_COUNT = 44;
const RIB_HEIGHTS = Array.from({ length: RIB_COUNT }, (_, i) => {
  const t = i / (RIB_COUNT - 1);
  const wave1 = 0.5 + 0.5 * Math.sin(t * Math.PI * 2.4 + 0.6);
  const wave2 = 0.5 + 0.5 * Math.sin(t * Math.PI * 7.3);
  return Math.min(100, Math.round(36 + 40 * wave1 + 16 * wave2));
});

const RESEARCH_PAPERS = [
  {
    id: "paper-1",
    title: "Sustainable Drone Surveillance System Using Eternal Vertex Cover and Periodic Charging",
    venue: "COMSNETS / Conference",
    year: "2025",
    summary:
      "This paper investigates the design of a sustainable drone surveillance system that leverages the eternal vertex cover problem and periodic charging strategies to optimize coverage and energy efficiency.",
    link: "https://ieeexplore.ieee.org/document/10885646",
  },
  {
    id: "paper-2",
    title: "Intelligent Drone Surveillance with Efficient Charging Mechanism Using Eternal Vertex Cover and p-Dispersion",
    venue: "ACM / Journal",
    year: "2025",
    summary:
      "This paper explores the application of eternal vertex cover and p-dispersion techniques to design an intelligent drone surveillance system with efficient charging mechanisms.",
    link: "https://dl.acm.org/doi/10.1145/3742439",
  },
];

export default function Work() {
  const containerRef = useRef(null);
  const haloContainerRef = useRef(null);
  const pillRef = useRef(null);
  const location = useLocation();

  const { currentTheme, activeTheme, THEME_PRESETS } = useTheme();

  const [activeCard, setActiveCard] = useState(location.state?.activeCard || null);
  const [researchOpen, setResearchOpen] = useState(false);
  const [pillText, setPillText] = useState("");

  const [selectedProject, setSelectedProject] = useState(
    location.state?.activeCard === "uiux" ? location.state.projectId : null
  );
  const [selectedFrontendProject, setSelectedFrontendProject] = useState(
    location.state?.activeCard === "frontend" ? location.state.projectId : null
  );
  const [selectedGraphicProject, setSelectedGraphicProject] = useState(
    location.state?.activeCard === "graphics" ? location.state.projectId : null
  );

  const uiuxProjects = [
    { id: "travel", label: "UX Case Study", img: wayfare, tilt: "tilt1" },
    { id: "bakery", label: "Web UI Design", img: iunides, tilt: "tilt2" },
    { id: "saas", label: "Landing Page", img: nuvexcover, tilt: "tilt4" },
    { id: "event", label: "UX Case Study", img: evento, tilt: "tilt3" },
  ];

  const frontendProjects = [
    { id: "ecommerce", label: "LifeStyle Ecommerce", img: iunidev, tilt: "tilt1" },
    { id: "ayurved", label: "Ayurveda Website", img: ayurnest, tilt: "tilt2" },
    { id: "fintech", label: "Fintech SaaS Website", img: nuvexcover, tilt: "tilt4" },
    { id: "brand", label: "Packaging Brand", img: bio, tilt: "tilt3" },
  ];

  const graphicsProjects = [
    { id: "bakery", label: "Bakery Branding", img: petty, tilt: "tilt1" },
    { id: "verd", label: "Brand Identity", img: verd, tilt: "tilt2" },
    { id: "poster", label: "Poster Designs", img: postercover, tilt: "tilt3" },
  ];

  const landingCards = [
    { id: "uiux", img: evento1, label: "UI/UX", tilt: "tilt1", hint: "Explore the blueprints" },
    { id: "frontend", img: devcover, label: "Web Dev", tilt: "tilt2", hint: "Straight from VS Code" },
    { id: "graphics", img: gdcover, label: "Graphic Design", tilt: "tilt3", hint: "View the visuals" },
    { id: "research", img: researchcover, label: "Research", tilt: "tilt4", hint: "Read my papers" },
  ];

  // 1. Preload distinct audio buffers into memory
  useEffect(() => {
    sounds.loadSound("miniHover", miniHoverSfx);
    sounds.loadSound("miniClick", miniClickSfx);
    sounds.loadSound("marqueeHover", marqueeHoverSfx);
    sounds.loadSound("marqueeClick", marqueeClickSfx);
    sounds.loadSound("paperHover", paperHoverSfx);
    sounds.loadSound("generalClick", generalClickSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  // 2. Landing mini card hover handler
  const showPill = (e, text) => {
    sounds.play("miniHover", 0.25);
    const pill = pillRef.current;
    if (!pill) return;
    setPillText(text);
    gsap.set(pill, { x: e.clientX, y: e.clientY });
    gsap.to(pill, { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.8)", overwrite: "auto" });
  };

  const movePill = (e) => {
    const pill = pillRef.current;
    if (!pill) return;
    gsap.to(pill, { x: e.clientX, y: e.clientY, duration: 0.1, ease: "none", overwrite: "auto" });
  };

  const hidePill = () => {
    const pill = pillRef.current;
    if (!pill) return;
    gsap.to(pill, { opacity: 0, scale: 0.6, duration: 0.2, ease: "power2.in", overwrite: "auto" });
  };

  useEffect(() => {
    if (pillRef.current) {
      gsap.set(pillRef.current, { xPercent: -50, yPercent: -50, scale: 0.6, opacity: 0 });
    }
  }, [activeCard]);

  // Research modal scroll lock and Esc key
  useEffect(() => {
    if (!researchOpen) return;
    const onKey = (e) => e.key === "Escape" && setResearchOpen(false);
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [researchOpen]);

  // Halftone spotlight cursor logic
  useEffect(() => {
    const container = haloContainerRef.current;
    if (!container) return;

    let idleTimeout;

    const activate = () => {
      container.classList.add("cursor-active");
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        container.classList.remove("cursor-active");
      }, 900);
    };

    const deactivate = () => {
      clearTimeout(idleTimeout);
      container.classList.remove("cursor-active");
    };

    const handleMove = (e) => {
      gsap.to(container, {
        "--mx": `${e.clientX}px`,
        "--my": `${e.clientY}px`,
        duration: 0.35,
        ease: "power2.out",
        overwrite: "auto",
      });
      activate();

      const currentGlowPreset = THEME_PRESETS[activeTheme]?.glows || THEME_PRESETS[0].glows;
      const glowTints = [
        hexToRgb(currentGlowPreset.g1),
        hexToRgb(currentGlowPreset.g2),
        hexToRgb(currentGlowPreset.g3),
      ];

      const px = (e.clientX / window.innerWidth) * 100;
      const py = (e.clientY / window.innerHeight) * 100;

      const weights = ZONE_POSITIONS.map((pos) => {
        const dx = px - pos.x;
        const dy = py - pos.y;
        return 1 / (dx * dx + dy * dy + 45);
      });
      const totalWeight = weights.reduce((sum, w) => sum + w, 0);

      const blended = glowTints.reduce(
        (acc, c, i) => {
          const w = weights[i] / totalWeight;
          return { r: acc.r + c.r * w, g: acc.g + c.g * w, b: acc.b + c.b * w };
        },
        { r: 0, g: 0, b: 0 }
      );

      const finalR = Math.round(blended.r);
      const finalG = Math.round(blended.g);
      const finalB = Math.round(blended.b);

      container.style.setProperty("--glow-color", `rgb(${finalR}, ${finalG}, ${finalB})`);
      container.style.setProperty("--glow-color-soft", `rgba(${finalR}, ${finalG}, ${finalB}, 0.28)`);
    };

    const handleWindowLeave = (e) => {
      if (!e.relatedTarget && !e.toElement) deactivate();
    };

    window.addEventListener("pointermove", handleMove);
    document.addEventListener("mouseout", handleWindowLeave);
    window.addEventListener("blur", deactivate);

    return () => {
      window.removeEventListener("pointermove", handleMove);
      document.removeEventListener("mouseout", handleWindowLeave);
      window.removeEventListener("blur", deactivate);
      clearTimeout(idleTimeout);
    };
  }, [activeTheme, THEME_PRESETS]);

  // Entrance animations
  useEffect(() => {
    let ctx = gsap.context(() => {
      const heading = containerRef.current?.querySelector(".section-text h1");
      const subtext = containerRef.current?.querySelector(".section-text p");
      const cards = containerRef.current?.querySelectorAll(".landing-cards .mini-card");
      const viewport = containerRef.current?.querySelector(".marquee-viewport");

      const tl = gsap.timeline();

      if (heading) {
        tl.fromTo(
          heading,
          { opacity: 0, y: 36 },
          { opacity: 1, y: 0, duration: 0.65, ease: "power3.out" }
        );
      }

      if (subtext) {
        tl.fromTo(
          subtext,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" },
          "-=0.35"
        );
      }

      if (cards?.length) {
        tl.fromTo(
          cards,
          { opacity: 0, yPercent: 60, scale: 0.94 },
          {
            opacity: 1,
            yPercent: 0,
            scale: 1,
            duration: 0.85,
            stagger: 0.12,
            ease: "power3.out",
            clearProps: "yPercent,scale,opacity",
          },
          "-=0.25"
        );
      }

      if (viewport) {
        tl.fromTo(
          viewport,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
          "-=0.3"
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, [activeCard]);

  // 3. Distinct Landing mini-card click handler
  const handleLandingCardClick = (id) => {
    sounds.play("miniClick", 0.4);
    hidePill();
    if (id === "research") setResearchOpen(true);
    else setActiveCard(id);
  };

  // 4. Distinct Marquee card click handler
  const handleMarqueeCardClick = (category, id) => {
    sounds.play("marqueeClick", 0.4);
    if (category === "uiux") setSelectedProject(id);
    if (category === "frontend") setSelectedFrontendProject(id);
    if (category === "graphics") setSelectedGraphicProject(id);
  };

  // Detail subpage views
  if (selectedProject) {
    return (
      <UIUXPage 
        project={selectedProject} 
        onBack={() => {
          sounds.play("generalClick", 0.3);
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
          setSelectedProject(null);
        }} 
      />
    );
  }

  if (selectedFrontendProject) {
    return (
      <Front 
        project={selectedFrontendProject} 
        onBack={() => {
          sounds.play("generalClick", 0.3);
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
          setSelectedFrontendProject(null);
        }} 
      />
    );
  }

  if (selectedGraphicProject) {
    return (
      <Graphics 
        project={selectedGraphicProject} 
        onBack={() => {
          sounds.play("generalClick", 0.3);
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
          setSelectedGraphicProject(null);
        }} 
      />
    );
  }

  const isLanding = !activeCard;

  return (
    <div
      className={`work-container${isLanding ? " landing-dark" : ""}`}
      ref={containerRef}
      style={{
        "--color-1": currentTheme.colors.c1,
        "--color-2": currentTheme.colors.c2,
        "--color-3": currentTheme.colors.c3,
        "--glow-1": currentTheme.glows.g1,
        "--glow-2": currentTheme.glows.g2,
        "--glow-3": currentTheme.glows.g3,
      }}
    >
      <div className={`halftone-container${isLanding ? " is-dark" : ""}`} ref={haloContainerRef}>
        <div className="gradient-wash" />

        {isLanding && (
          <>
            <div className="float-blob blob-1" />
            <div className="float-blob blob-2" />
            <div className="float-blob blob-3" />
            <div className="float-blob blob-4" />

            <div className="glass-ribs" aria-hidden="true">
              {RIB_HEIGHTS.map((h, i) => (
                <span className="glass-rib" key={i} style={{ "--h": `${h}%` }} />
              ))}
            </div>
          </>
        )}

        {!isLanding && (
          <>
            <div className="cursor-glow" />
            <div className="halftone-dots-layer" />
          </>
        )}
      </div>

      {isLanding ? (
        <>
          <div className="landing-section">
            <div className="section-text">
              <h1>
                Projects <span className="title-punch">Catalogue</span>
              </h1>
              <p>
                A collection of my projects that explore UI/UX design, frontend development, visual storytelling and research across different domains.
              </p>
            </div>

            <div className="project-cards landing-cards">
              {landingCards.map(({ id, img, label, tilt, hint }) => (
                <div
                  className={`mini-card ${tilt}`}
                  key={id}
                  role="button"
                  tabIndex={0}
                  onPointerEnter={(e) => showPill(e, hint)}
                  onPointerMove={movePill}
                  onPointerLeave={hidePill}
                  onClick={() => handleLandingCardClick(id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleLandingCardClick(id);
                    }
                  }}
                >
                  <span className="label">{label}</span>
                  <div className="card-media-frame">
                    <img
                      src={img}
                      alt={label}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hover cursor pill */}
          <div className="cursor-pill" ref={pillRef} aria-hidden="true">
            {pillText}
            {/* <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
              <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg> */}
          </div>

          {/* Research popup */}
          {researchOpen && (
            <div 
              className="research-overlay" 
              onClick={() => {
                sounds.play("generalClick", 0.25);
                setResearchOpen(false);
              }}
            >
              <div
                className="research-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="research-heading"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="research-close"
                  onClick={() => {
                    sounds.play("generalClick", 0.25);
                    setResearchOpen(false);
                  }}
                  aria-label="Close research"
                >
                  <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                </button>

                <h2 id="research-heading" className="research-title">
                  My <em>Research</em>
                </h2>
                {/* <p className="research-sub">
                  exploring modern navigation systems through glassmorphism, motion, and layered interaction design
                </p> */}

                <div className="research-list">
                  {RESEARCH_PAPERS.map((paper) => (
                    <article 
                      className="research-box" 
                      key={paper.id}
                      onMouseEnter={() => sounds.play("paperHover", 0.2)}
                    >
                      <div className="research-box-text">
                        <span className="research-meta">
                          {paper.venue}, {paper.year}
                        </span>
                        <h3>{paper.title}</h3>
                        <p>{paper.summary}</p>
                      </div>
                      <a
                        className="research-link"
                        href={paper.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => sounds.play("generalClick", 0.35)}
                      >
                        Read paper
                        <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                          <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="detail-page">
          <button 
            className="back-btn" 
            onClick={() => {
              sounds.play("generalClick", 0.3);
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
              setActiveCard(null);
            }} 
            aria-label="Go Back"
          >
            <svg
              className="back-btn-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 18L6 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 6H6V16"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {activeCard === "uiux" && (
            <div className="uiux-section">
              <div className="section-text">
                <h1>
                  Interface <span className="title-punch">Studies</span>
                </h1>
                <p>Explore projects covering case studies, wireframes, prototypes, and polished user interfaces.</p>
              </div>
              <div className="marquee-viewport">
                <div className="marquee-track">
                  {(window.innerWidth > 1024
                    ? [...uiuxProjects, ...uiuxProjects, ...uiuxProjects]
                    : uiuxProjects
                  ).map(({ id, label, img, tilt }, i) => (
                    <div
                      className={`mini-card marquee-card ${tilt}`}
                      key={`${id}-${i}`}
                      onMouseEnter={() => sounds.play("marqueeHover", 0.22)}
                      onClick={() => handleMarqueeCardClick("uiux", id)}
                    >
                      <div className="card-media-frame">
                        <img
                          src={img}
                          alt={label}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <span className="label2">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeCard === "frontend" && (
            <div className="frontend-section">
              <div className="section-text">
                <h1>
                  Code <span className="title-punch">Canvas</span>
                </h1>
                <p>Frontend projects exploring responsive layouts, reusable components, and engaging interactions.</p>
              </div>
              <div className="marquee-viewport">
                <div className="marquee-track">
                  {(window.innerWidth > 1024
                    ? [...frontendProjects, ...frontendProjects, ...frontendProjects]
                    : frontendProjects
                  ).map(({ id, label, img, tilt }, i) => (
                    <div
                      className={`mini-card marquee-card ${tilt}`}
                      key={`${id}-${i}`}
                      onMouseEnter={() => sounds.play("marqueeHover", 0.22)}
                      onClick={() => handleMarqueeCardClick("frontend", id)}
                    >
                      <div className="card-media-frame">
                        <img
                          src={img}
                          alt={label}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="marquee-card-footer">
                        <span className="label2">{label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeCard === "graphics" && (
            <div className="graphics-section">
              <div className="section-text">
                <h1>
                  Visual <span className="title-punch">Archive</span>
                </h1>
                <p>A selection of creative work exploring brand identity, marketing campaigns, and visual storytelling.</p>
              </div>
              <div className="marquee-viewport">
                <div className="marquee-track">
                  {(window.innerWidth > 1024
                    ? [...graphicsProjects, ...graphicsProjects, ...graphicsProjects]
                    : graphicsProjects
                  ).map(({ id, label, img, tilt }, i) => (
                    <div
                      className={`mini-card marquee-card ${tilt}`}
                      key={`${id}-${i}`}
                      onMouseEnter={() => sounds.play("marqueeHover", 0.22)}
                      onClick={() => handleMarqueeCardClick("graphics", id)}
                    >
                      <div className="card-media-frame">
                        <img
                          src={img}
                          alt={label}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="marquee-card-footer">
                        <span className="label2">{label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}